// Typ: FindAltitudeToHypotenuseTask
// Dział 6 → Trójkąty → Trójkąt prostokątny
// Cel: Dla danych przyprostokątnych a, b oblicz wysokość opuszczoną na przeciwprostokątną h_c.
// Wzory: c = √(a²+b²),  (1/2)ab = (1/2)c·h_c  ⇒  h_c = ab / c
// Wejście: RootFractionInput (√ i ułamki), wynik formatowany z zracjonalizowanym mianownikiem.

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== utils (jak w Heronie) =====
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// Dla n ≥ 1: √n = outside * √inside, gdzie inside square-free
function simplifySqrtInt(n) {
  if (n <= 0) return { outside: 0, inside: 1 };
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    const sq = i * i;
    while (inside % sq === 0) {
      outside *= i;
      inside /= sq;
    }
  }
  return { outside, inside };
}

// Format: (coeff * √root) / den ; gdy root==1 → zwykła liczba/ulamek/mieszana
function formatCoeffWithRoot(coeff, den, root) {
  const d = gcd(Math.abs(coeff), Math.abs(den));
  let num = coeff / d;
  let denS = den / d;

  if (!root || root === 1) {
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);
    if (denS === 1) return `${num}`;
    if (absNum < denS) return `${sign}\\dfrac{${absNum}}{${denS}}`;
    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`;
  }

  const sign = num < 0 ? "-" : "";
  const nabs = Math.abs(num);
  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// ===== generator =====
export function generateTask() {
  // ogólne a,b → często wyjdzie √; zakres „przyjemny”
  const a = rnd(3, 18);
  const b = rnd(3, 18);
  const sum = a * a + b * b; // pod √
  // c = √sum (do wyświetlenia/rachunku w wyjaśnieniu)
  // h_c = ab / c  = (ab * √sum) / sum  (zracjonalizowaliśmy mianownik)
  const { outside, inside } = simplifySqrtInt(sum);
  // (ab * outside * √inside) / sum  → uprość
  const coeff = a * b * outside;
  const den = sum;
  const latex = formatCoeffWithRoot(coeff, den, inside);

  return {
    type: "FindAltitudeToHypotenuseTask",
    data: { a, b, sum, latex, outside, inside } // trzymamy też części do wyjaśnienia
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a, b } = data;
  return (
    <div style={{ marginBottom: 8 }}>
      W trójkącie prostokątnym o przyprostokątnych
      <InlineMath math={`\\;a=${a}`} /> i <InlineMath math={`\\;b=${b}`} />
      {" "}oblicz wysokość opuszczoną na przeciwprostokątną
      {" "}<InlineMath math={'h_c'} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'h_c'}
        placeholder="np. (12\\sqrt{5})/25, 6, 3\\tfrac{1}{2}"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === 'object' ? userAnswer?.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') return false;

  // wartość oczekiwana: h_c = ab / √(a²+b²)
  const { a, b, sum } = data;
  const expected = (a * b) / Math.sqrt(sum);

  // 1) standardowy parse z RootFractionInput
  let parsed =
    typeof userAnswer === 'object' && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba z przecinkiem
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === 'string') {
    const asNumber = Number(raw.replace(',', '.'));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptuj ±0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { latex } = data; // już zracjonalizowane i uproszczone
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`h_c = ${latex}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, sum, outside, inside, latex } = data;
  const a2 = a * a, b2 = b * b;

  // uproszczona postać c
  let cLatex;
  if (inside === 1) {
    cLatex = `${outside}`;
  } else if (outside === 1) {
    cLatex = `\\sqrt{${inside}}`;
  } else {
    cLatex = `${outside}\\sqrt{${inside}}`;
  }

  return (
    <div style={{ textAlign: 'left', lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Obliczamy przeciwprostokątną z twierdzenia Pitagorasa.</div>
      <BlockMath math={`c^2 = a^2 + b^2 = ${a}^2 + ${b}^2 = ${a2} + ${b2} = ${sum}`} />
      {cLatex === `\\sqrt{${sum}}` ? (
        <BlockMath math={`c = \\sqrt{${sum}}`} />
      ) : (
        <BlockMath math={`c = \\sqrt{${sum}} = ${cLatex}`} />
      )}

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Korzystamy z równości pól:
        {" "}<InlineMath math={'\\tfrac12 ab = \\tfrac12 c\\,h_c'} />, więc{" "}
        <InlineMath math={'h_c = \\dfrac{ab}{c}'} />.
      </div>
      <BlockMath math={`h_c = \\dfrac{${a}\\cdot ${b}}{${cLatex}}`} />

      <div style={{ marginTop: 8 }}>
        <strong>Krok 3.</strong> Zapisujemy wynik w postaci uproszczonej (zracjonalizowany mianownik).
      </div>
      <BlockMath math={`h_c = ${latex}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'object' ? userAnswer?.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, 'h_c')} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
